import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

/** Same options as Classes Attendance form (FormConfigurations — do not change form file). */
const ATTENDANCE_STATUSES = ["Present", "Absent"];

function normalizeListPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

function studentDisplayName(st) {
  if (!st) return "";
  const parts = [st.first_name, st.middle_name, st.last_name].filter(
    (x) => x && String(x).trim() !== ""
  );
  if (parts.length) return parts.join(" ");
  if (st.full_name) return String(st.full_name);
  return st.student_id ? String(st.student_id) : "";
}

/** Format YYYY-MM-DD or datetime-ish strings for the Class date line. */
function formatDateForAttendanceLabel(input) {
  if (input == null) return null;
  const str = String(input).trim();
  if (!str) return null;
  const ymd = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const d = new Date(`${ymd}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
  const d = new Date(str);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return str;
}

/**
 * Prefer batch class schedule date; if null, use class record session `start_time` date;
 * else batch program `start_date`.
 */
function resolveDisplayedClassDate(batchClass, classRecord) {
  const bcDate = batchClass?.class_date;
  if (bcDate != null && String(bcDate).trim() !== "") {
    return formatDateForAttendanceLabel(bcDate) ?? "—";
  }
  const nestedBcDate = classRecord?.batch_class?.class_date;
  if (nestedBcDate != null && String(nestedBcDate).trim() !== "") {
    return formatDateForAttendanceLabel(nestedBcDate) ?? "—";
  }
  const recordStart = classRecord?.start_time;
  if (recordStart != null && String(recordStart).trim() !== "") {
    const str = String(recordStart).trim();
    const m = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) {
      const labeled = formatDateForAttendanceLabel(m[1]);
      if (labeled) return labeled;
    }
    const fromDt = formatDateForAttendanceLabel(str);
    if (fromDt) return fromDt;
  }
  const batchStart = batchClass?.batch?.start_date;
  if (batchStart != null && String(batchStart).trim() !== "") {
    return formatDateForAttendanceLabel(batchStart) ?? "—";
  }
  return "—";
}

/** Returns YYYY-MM-DD from datetime/date-like input, else empty string. */
function toYmdDate(input) {
  if (input == null) return "";
  const s = String(input).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function getLocalTodayYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isFutureYmd(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  return ymd > getLocalTodayYmd();
}

function isBatchStudentActiveForClassDate(batchStudent, classDateYmd) {
  if (!batchStudent) return false;
  const status = String(batchStudent?.batch_status || "")
    .trim()
    .toLowerCase();
  if (status !== "completed") return true;

  const completedYmd = toYmdDate(batchStudent?.completion_date);
  if (!classDateYmd) return false;
  if (!completedYmd) return false;
  return classDateYmd <= completedYmd;
}

export default function BatchClassAttendanceMarking() {
  const { batchClassId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const orgId = userData?.organization?.organization_id;
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [batchClassMeta, setBatchClassMeta] = useState(null);
  /** { organization_ems_student_id, label, organization_ems_class_attendance_id | null, attendance_status } */
  const [rows, setRows] = useState([]);

  /** When opened from Class records list, URL includes `class_record_id`; return there instead of Batch classes. */
  const listBackPath = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const cr = search.get("class_record_id");
    if (
      cr != null &&
      String(cr).trim() !== "" &&
      String(cr) !== "-"
    ) {
      return "/class-records";
    }
    return "/batch-classes";
  }, [location.search]);

  const listBackLabel =
    listBackPath === "/class-records"
      ? "Back to batch records"
      : "Back to Batch Classes";

  const load = useCallback(async () => {
    if (!batchClassId || !orgId) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = { organization_id: orgId, per_page: "all" };
      const search = new URLSearchParams(location.search);
      const classRecordId = search.get("class_record_id");

      const [bcRes, attRes] = await Promise.all([
        axios.get(`${MAIN_URL}/api/ems/batch-classes/${batchClassId}`, {
          headers,
          params: { organization_id: orgId },
        }),
        axios.get(`${MAIN_URL}/api/ems/classes-attendance`, { headers, params }),
      ]);

      const bcPayload = bcRes.data?.data ?? bcRes.data;
      const batchClass = bcPayload?.data ?? bcPayload;
      if (!batchClass?.organization_ems_batch_class_id) {
        toast.error("Batch class not found.");
        navigate(listBackPath);
        return;
      }

      const batchId = batchClass.organization_ems_batch_id;
      let forBatch = [];
      let classRecord = null;
      if (
        classRecordId !== null &&
        classRecordId !== undefined &&
        String(classRecordId).trim() !== "" &&
        String(classRecordId) !== "-"
      ) {
        try {
          const crRes = await axios.get(
            `${MAIN_URL}/api/ems/class-records/${encodeURIComponent(
              String(classRecordId)
            )}`,
            {
              headers,
              params: { organization_id: orgId },
            }
          );
          const crPayload = crRes.data?.data ?? crRes.data;
          classRecord = crPayload?.data ?? crPayload;
          const rawFromRecord = Array.isArray(classRecord?.batch_student)
            ? classRecord.batch_student
            : Array.isArray(classRecord?.batch_students)
              ? classRecord.batch_students
              : [];
          forBatch = rawFromRecord;
        } catch (err) {
          // Non-blocking: fall back to generic batch-students endpoint
          console.error("Class record students fetch failed:", err);
        }
      }
      if (!Array.isArray(forBatch) || forBatch.length === 0) {
        const bsParams = {
          ...params,
          ...(batchId != null && batchId !== ""
            ? { organization_ems_batch_id: batchId }
            : {}),
        };
        const bsRes = await axios.get(`${MAIN_URL}/api/ems/batch-students`, {
          headers,
          params: bsParams,
        });
        const batchStudentsRaw = normalizeListPayload(
          bsRes.data?.data ?? bsRes.data
        );
        forBatch = batchStudentsRaw.filter(
          (r) =>
            Number(r?.organization_ems_batch_id) === Number(batchId) ||
            Number(r?.batch?.organization_ems_batch_id) === Number(batchId)
        );
      }

      const classDateYmd =
        toYmdDate(classRecord?.start_time) ||
        toYmdDate(classRecord?.batch_class?.class_date) ||
        toYmdDate(batchClass?.class_date) ||
        "";
      const eligibleBatchStudents = forBatch.filter((bs) =>
        isBatchStudentActiveForClassDate(bs, classDateYmd)
      );

      const attendanceRaw = normalizeListPayload(
        attRes.data?.data ?? attRes.data
      );
      const forClass = attendanceRaw.filter(
        (a) =>
          Number(a?.organization_ems_batch_class_id) ===
          Number(batchClassId)
      );

      /** Multiple rows per student (different dates/sessions): pick the row for *this* screen. */
      const pickAttendanceRowForStudent = (list) => {
        if (!list?.length) return null;
        if (list.length === 1) return list[0];
        const crParam =
          classRecordId != null &&
          String(classRecordId).trim() !== "" &&
          String(classRecordId) !== "-"
            ? Number(classRecordId)
            : null;
        if (crParam != null && !Number.isNaN(crParam)) {
          const byRec = list.find(
            (a) => Number(a?.organization_ems_batch_class_record_id) === crParam
          );
          if (byRec) return byRec;
        }
        if (classRecord) {
          const ymd =
            toYmdDate(classRecord?.start_time) ||
            toYmdDate(classRecord?.batch_class?.class_date) ||
            "";
          if (ymd) {
            const byDate = list.find(
              (a) => toYmdDate(a?.attendance_date) === ymd
            );
            if (byDate) return byDate;
          }
        }
        const ymdClass = toYmdDate(batchClass?.class_date);
        if (ymdClass) {
          const byDate = list.find(
            (a) => toYmdDate(a?.attendance_date) === ymdClass
          );
          if (byDate) return byDate;
        }
        return [...list].sort(
          (a, b) =>
            new Date(b?.attendance_date || 0).getTime() -
            new Date(a?.attendance_date || 0).getTime()
        )[0];
      };

      const byStudentId = new Map();
      forClass.forEach((a) => {
        const sid = a?.organization_ems_student_id;
        if (sid == null) return;
        const n = Number(sid);
        if (!byStudentId.has(n)) byStudentId.set(n, []);
        byStudentId.get(n).push(a);
      });

      const built = eligibleBatchStudents
        .map((bs) => {
          const st = bs?.student;
          const sid = bs?.organization_ems_student_id ?? st?.organization_ems_student_id;
          if (sid == null) return null;
          const candidates = byStudentId.get(Number(sid)) || [];
          const existing = pickAttendanceRowForStudent(candidates);
          const label = studentDisplayName(st) || `Student #${sid}`;
          const existingRecordId =
            existing?.organization_ems_batch_class_record_id != null &&
            existing?.organization_ems_batch_class_record_id !== ""
              ? Number(existing.organization_ems_batch_class_record_id)
              : null;
          return {
            organization_ems_student_id: Number(sid),
            label,
            student_id_code: st?.student_id ? String(st.student_id) : "",
            organization_ems_class_attendance_id:
              existing?.organization_ems_class_attendance_id ?? null,
            /** Used on save: PUT only if this id belongs to the class record we are marking. */
            attendance_row_batch_class_record_id:
              existingRecordId != null && !Number.isNaN(existingRecordId)
                ? existingRecordId
                : null,
            attendance_status:
              existing?.attendance_status && ATTENDANCE_STATUSES.includes(existing.attendance_status)
                ? existing.attendance_status
                : "Present",
          };
        })
        .filter(Boolean);

      built.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );

      setBatchClassMeta({
        topic:
          classRecord?.topic ||
          classRecord?.batch_class?.topic ||
          classRecord?.batchClass?.topic ||
          batchClass.topic ||
          batchClass.module ||
          "—",
        class_date: resolveDisplayedClassDate(batchClass, classRecord),
        attendance_date_api:
          toYmdDate(classRecord?.start_time) ||
          toYmdDate(classRecord?.batch_class?.class_date) ||
          toYmdDate(batchClass?.class_date) ||
          "",
        batch_class_record_id:
          classRecord?.organization_ems_batch_class_record_id ??
          classRecord?.organization_ems_class_record_id ??
          "",
        batch_label:
          batchClass?.batch?.batch_name && batchClass?.batch?.batch_code
            ? `${batchClass.batch.batch_name} (${batchClass.batch.batch_code})`
            : batchClass?.batch?.batch_name || "—",
      });
      setRows(built);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load attendance data.");
      navigate(listBackPath);
    } finally {
      setLoading(false);
    }
  }, [batchClassId, orgId, token, navigate, location.search, listBackPath]);

  useEffect(() => {
    load();
  }, [load]);

  const setAttendanceStatus = (studentId, status) => {
    if (!ATTENDANCE_STATUSES.includes(status)) return;
    setRows((prev) =>
      prev.map((r) =>
        r.organization_ems_student_id === studentId
          ? { ...r, attendance_status: status }
          : r
      )
    );
  };

  const handleSave = async () => {
    if (!orgId || !batchClassId) return;
    const invalid = rows.find(
      (r) => !r.attendance_status || !ATTENDANCE_STATUSES.includes(r.attendance_status)
    );
    if (invalid) {
      toast.error("Each row must have a valid attendance status.");
      return;
    }

    setSaving(true);
    const headers = { Authorization: `Bearer ${token}` };
    const classRecordIdRaw = batchClassMeta?.batch_class_record_id;
    const attendanceDateRaw = batchClassMeta?.attendance_date_api;
    if (attendanceDateRaw && isFutureYmd(String(attendanceDateRaw))) {
      toast.error("Attendance cannot be marked for a future class date.");
      setSaving(false);
      return;
    }
    const classRecordNum =
      classRecordIdRaw !== "" &&
      classRecordIdRaw != null &&
      classRecordIdRaw !== undefined
        ? Number(classRecordIdRaw)
        : null;

    try {
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i];
        const body = {
          organization_id: orgId,
          organization_entity_id: null,
          organization_ems_batch_class_id: Number(batchClassId),
          organization_ems_student_id: r.organization_ems_student_id,
          attendance_status: r.attendance_status,
          remarks: "",
          ...(classRecordNum != null && !Number.isNaN(classRecordNum)
            ? { organization_ems_batch_class_record_id: classRecordNum }
            : {}),
          ...(attendanceDateRaw ? { attendance_date: attendanceDateRaw } : {}),
        };

        await axios.post(`${MAIN_URL}/api/ems/classes-attendance`, body, {
          headers,
        });
      }
      toast.success("Attendance saved.");
      navigate(listBackPath);
    } catch (e) {
      console.error(e);
      toast.error(
        e.response?.data?.message || "Save failed. Check required fields."
      );
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(
    () => "Attendance marking",
    []
  );

  if (!orgId) {
    return (
      <Box p={4}>
        <Typography>Select an organization to continue.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(listBackPath)}
        sx={{ mb: 2 }}
      >
        {listBackLabel}
      </Button>

      <Typography variant="h5" fontWeight={600} gutterBottom>
        {title}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Batch class
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {batchClassMeta?.batch_label ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Topic / module
            </Typography>
            <Typography variant="body1">{batchClassMeta?.topic ?? "—"}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Class date
            </Typography>
            <Typography variant="body1">{batchClassMeta?.class_date ?? "—"}</Typography>
          </Paper>

          {rows.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography color="text.secondary">
                No students enrolled in this batch for this class, or batch students could not be loaded.
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              {/* <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1 }}>
                Students (alphabetical) — set status and remarks as in Class Attendance
              </Typography> */}
              <TableContainer sx={{ maxHeight: "70vh" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="26%">Student</TableCell>
                      <TableCell width="14%">Student ID</TableCell>
                      <TableCell width="60%">Attendance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.organization_ems_student_id}>
                        <TableCell>{r.label}</TableCell>
                        <TableCell>{r.student_id_code || "—"}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: { xs: 0.25, sm: 1 },
                              alignItems: "center",
                              py: 0.5,
                            }}
                          >
                            {ATTENDANCE_STATUSES.map((status) => (
                              <FormControlLabel
                                key={status}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={r.attendance_status === status}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setAttendanceStatus(
                                          r.organization_ems_student_id,
                                          status
                                        );
                                      }
                                    }}
                                    color="primary"
                                  />
                                }
                                label={status}
                                sx={{
                                  mr: 0,
                                  ml: 0,
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "0.8125rem",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || rows.length === 0}
              onClick={handleSave}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : "Save attendance"}
            </Button>
            <Button variant="outlined" onClick={() => navigate(listBackPath)}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
